import {
  Box,
  Flex,
  KdsCallout,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import {
  RiCalendarScheduleLine,
  RiIndeterminateCircleLine,
} from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import dayjs from "dayjs";
import { useState } from "react";
import { OrderItemPageActivityFeed } from "@/components/OrderItemActivityFeed/OrderItemPageActivityFeed";
import { OrderItemStatus } from "@/components/OrderItemStatus";
import { useIsOMS } from "@/hooks/useIsOMS";
import { ModalCancelOrderItem } from "@/modals/ModalCancelOrderItem";
import { ModalPurchaseOrderForm } from "@/modals/ModalPurchaseOrderForm";
import { ModalRushOrder } from "@/modals/ModalRushOrder";
import { type OrderItemDetailsContentProps } from "./OrderItemDetailsContent.types";
import { OrderItemDetailsTabs } from "./OrderItemDetailsTabs";

export const OrderItemDetailsContent = ({
  hasAModificationRequest,
  handleCancelModalOpen,
  isCancelModalOpen,
  isLoading,
  order,
  orderItem,
  isRushOrderUIDisplayed,
  handleRefetchOrderItemDetails,
}: OrderItemDetailsContentProps) => {
  const { t } = useTranslate();
  const isOMS = useIsOMS();

  const [isErrorCalloutVisible, setIsErrorCalloutVisible] = useState(false);
  const [isOpenRushOrder, setIsOpenRushOrder] = useState(false);
  const [isModalPurchaseOrderOpen, setIsModalPurchaseOrderOpen] =
    useState(false);

  return (
    <VStack gap="sp-xl">
      {!isOMS && (
        <KdsCallout
          title={t("callout.error.title")}
          iconLeft={RiIndeterminateCircleLine}
          state="error"
          visible={isErrorCalloutVisible}
          handleClose={() => setIsErrorCalloutVisible(false)}
        >
          <KdsText>{t("callout.error.content")}</KdsText>
        </KdsCallout>
      )}

      {isOMS && isRushOrderUIDisplayed && (
        <KdsCallout
          iconLeft={RiCalendarScheduleLine}
          visible={!!isRushOrderUIDisplayed}
          state="warning"
          has="button"
          position="right"
          buttonProps={{
            children: t(
              "order.myOrdersItemDetails.callout.warningRushOrder.button"
            ),
            onPress: () => setIsOpenRushOrder(true),
          }}
          title={t("order.myOrdersItemDetails.callout.warningRushOrder.title", {
            days: dayjs(orderItem?.sandboxItem.deliveryDate).format(
              "DD/MM/YYYY"
            ),
          })}
        >
          <KdsText>
            t( "order.myOrdersItemDetails.callout.warningRushOrder.subtitle" )
          </KdsText>
        </KdsCallout>
      )}

      <Flex direction={{ base: "column", xl: "row" }} gap="sp-xl">
        <Box w={{ base: "100%", xl: "50%" }} px="sp-md" pb="sp-xl2" pt="sp-xs">
          <VStack gap="sp-md">
            {!hasAModificationRequest && (
              <VStack gap="sp-md">
                <OrderItemStatus
                  orderItem={orderItem}
                  isLoading={isLoading}
                  orderItems={orderItem.order.OrderItem || []}
                  onError={() => setIsErrorCalloutVisible(true)}
                />
              </VStack>
            )}

            {orderItem && (
              <OrderItemPageActivityFeed
                orderId={order.id}
                orderItemId={orderItem.id}
              />
            )}
          </VStack>
        </Box>

        <OrderItemDetailsTabs
          hasAModificationRequest={hasAModificationRequest}
          handleCancelModalOpen={handleCancelModalOpen}
          isLoading={isLoading}
          order={order}
          orderItem={orderItem}
        />
      </Flex>

      <ModalCancelOrderItem
        handleErrorOpenChange={setIsErrorCalloutVisible}
        isOpen={orderItem && isCancelModalOpen}
        onOpenChange={handleCancelModalOpen}
        order={order}
        orderItem={orderItem}
      />

      <ModalPurchaseOrderForm
        isOpen={isModalPurchaseOrderOpen}
        setOpenState={setIsModalPurchaseOrderOpen}
        orderId={order.id}
      />

      <ModalRushOrder
        isOpen={orderItem && isOMS && isOpenRushOrder}
        setIsOpen={setIsOpenRushOrder}
        orderItem={orderItem}
        refecthOrderItems={handleRefetchOrderItemDetails}
      />
    </VStack>
  );
};
